import React from "react";
import { Link } from "react-router-dom";
import { X, Plus } from "lucide-react";
import Footer from "../components/Footer";
import { useCompare } from "../context/CompareContext";
import { useCart } from "../context/CartContext";
import { fmtPrice } from "../lib/api";
import { handleImgError } from "../lib/imgFallback";
import { toast } from "sonner";

const ROWS = [
  { label: "Category", key: (p) => p.category },
  { label: "Concentration / Size", key: (p) => p.size },
  { label: "Purity", key: (p) => p.purity },
  { label: "Molecular Weight", key: (p) => p.molecular_weight },
  { label: "Research Areas", key: (p) => (p.research_areas || []).join(", ") },
  { label: "Availability", key: (p) => (p.stock <= 10 ? `Only ${p.stock} left` : "In stock") },
  { label: "Price", key: (p) => fmtPrice(p.price) },
];

export default function Compare() {
  const { items, remove } = useCompare();
  const { addItem } = useCart();

  // Dynamic grid columns based on number of items (2, 3, or 4)
  const productCols = items.length || 1;
  const cellStyle = { gridTemplateColumns: `repeat(${productCols}, minmax(0, 1fr))` };

  return (
    <div className="pt-20 min-h-screen flex flex-col bg-[#0F0F10]">
      <section className="mx-auto max-w-[1500px] w-full px-4 sm:px-10 py-10 sm:py-16 flex-1">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#4ADE80] mb-4 sm:mb-5">Side by Side</p>
        <h1 className="font-serif-editorial text-4xl sm:text-5xl md:text-6xl text-[#F5F5F5]">Compare Compounds</h1>

        {items.length === 0 ? (
          <div className="mt-14 border border-white/10 p-10 sm:p-16 text-center">
            <p data-testid="compare-empty" className="text-[#A8A8A8] uppercase tracking-[0.18em] text-sm">No compounds selected to compare.</p>
            <Link to="/products" className="btn-press mt-8 inline-block bg-[#4ADE80] text-[#0F0F10] uppercase tracking-[0.16em] text-[11px] px-8 py-4 hover:bg-[#6FE998]">Browse Catalog</Link>
          </div>
        ) : (
          <div data-testid="compare-table" className="mt-8 sm:mt-12">
            {/* Product header row: images + names */}
            <div className="grid gap-3 sm:gap-6" style={cellStyle}>
              {items.map((p) => (
                <div key={p.slug} className="relative">
                  <button
                    data-testid={`compare-page-remove-${p.slug}`}
                    onClick={() => remove(p.slug)}
                    aria-label={`Remove ${p.name}`}
                    className="absolute top-1 right-1 z-10 bg-[#0F0F10]/80 backdrop-blur rounded-full p-1 border border-white/10 text-[#A8A8A8] hover:text-[#F5F5F5]"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                  <Link to={`/products/${p.slug}`} className="block">
                    <div className="aspect-square w-full bg-[#0F0F10] border border-white/[0.06] overflow-hidden flex items-center justify-center">
                      <img src={p.image} alt={p.name} onError={handleImgError} className="max-h-full max-w-full object-contain vial-blend-soft" />
                    </div>
                    <h3 className="mt-3 font-display text-sm sm:text-lg text-[#F5F5F5] leading-tight line-clamp-2">{p.name}</h3>
                    <p className="mt-1 text-[10px] sm:text-xs uppercase tracking-[0.16em] text-[#4ADE80]">{fmtPrice(p.price)}</p>
                  </Link>
                </div>
              ))}
            </div>

            {/* Attribute rows */}
            <div className="mt-8 sm:mt-12 space-y-6 sm:space-y-8">
              {ROWS.map((r) => (
                <div key={r.label} className="border-t border-white/[0.06] pt-4 sm:pt-5">
                  <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#7a7a7a] mb-2 sm:mb-3">{r.label}</div>
                  <div className="grid gap-3 sm:gap-6" style={cellStyle}>
                    {items.map((p) => (
                      <div key={p.slug} className="text-[13px] sm:text-[14px] text-[#E5E5E5] font-light leading-relaxed break-words">
                        {r.key(p) || <span className="text-[#7a7a7a]">—</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Add-to-cart row */}
            <div className="mt-8 sm:mt-10 border-t border-white/[0.06] pt-6 sm:pt-8">
              <div className="grid gap-3 sm:gap-6" style={cellStyle}>
                {items.map((p) => (
                  <button
                    key={p.slug}
                    data-testid={`compare-add-${p.slug}`}
                    onClick={() => { addItem(p, 1); toast.success(`${p.name} added`); }}
                    className="btn-press bg-[#4ADE80] text-[#0F0F10] uppercase tracking-[0.14em] text-[10px] sm:text-[11px] px-3 sm:px-5 py-2 sm:py-2.5 hover:bg-[#6FE998] inline-flex items-center justify-center gap-1"
                  >
                    <Plus className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> Add
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>
      <Footer />
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
}
